import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@blackboard/shared';
import { usePresence } from '../state/presence';
import { useBoard } from '../state/board';
import { useAuth } from '../state/auth';
import { getTheme } from '../themes';
import { displayName, needsNicknameHint } from '../utils/name';
import { UsersIcon } from './icons';

export function UserList() {
  const users = usePresence((s) => s.users);
  const board = useBoard((s) => s.board);
  const me = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const arr = [...users.values()];
  const [menuOpen, setMenuOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  // 展示名只能落到机器形态（学号/邮箱/UUID）时，引导设置一次昵称；
  // 有用户名可展示（如 aaa789456）就不打扰。
  useEffect(() => {
    if (!me) return;
    try {
      if (localStorage.getItem('bb_nick_prompted') !== '1' && needsNicknameHint(me)) {
        localStorage.setItem('bb_nick_prompted', '1');
        setRenameOpen(true);
      }
    } catch { /* private mode */ }
  }, [me]);
  const menuRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  useEffect(() => {
    if (!overflowOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!overflowRef.current?.contains(e.target as Node)) setOverflowOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [overflowOpen]);

  if (!board) return null;
  const theme = getTheme(board.theme);

  return (
    <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
      <div
        className="flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-medium backdrop-blur-xl"
        style={{
          background: theme.surface,
          color: theme.surfaceText,
          border: `1px solid ${theme.surfaceBorder}`,
          boxShadow: theme.surfaceShadow,
        }}
      >
        <UsersIcon size={14} />
        <span>
          <span className="font-semibold">{arr.length}</span>
          <span className="ml-1" style={{ color: theme.surfaceTextMuted }}>
            在线
          </span>
        </span>
      </div>
      <div className="flex -space-x-2">
        {arr.slice(0, 8).map((u) => {
          const hl = usePresence.getState().highlightUserId === u.user_id;
          const isMe = me?.id === u.user_id;
          return (
            <div
              key={u.user_id}
              title={isMe ? `${displayName(u)}（你自己）` : `${displayName(u)}${hl ? '（已高亮）' : '（点击高亮其笔画）'}`}
              role={isMe ? undefined : 'button'}
              tabIndex={isMe ? -1 : 0}
              aria-pressed={isMe ? undefined : hl}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-transform ${isMe ? '' : 'cursor-pointer'}`}
              style={{
                background: u.color,
                color: readableOn(u.color),
                border: hl
                  ? `2px solid #a78bfa`
                  : `2px solid ${theme.surface}`,
                boxShadow: hl
                  ? `0 0 0 2px #a78bfa`
                  : theme.surfaceShadow,
                transform: hl ? 'scale(1.15)' : 'scale(1)',
              }}
              onClick={isMe ? undefined : () => {
                const cur = usePresence.getState().highlightUserId;
                usePresence.getState().setHighlight(cur === u.user_id ? null : u.user_id);
              }}
              onKeyDown={isMe ? undefined : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const cur = usePresence.getState().highlightUserId;
                  usePresence.getState().setHighlight(cur === u.user_id ? null : u.user_id);
                }
              }}
            >
              {firstGlyph(displayName(u))}
            </div>
          );
        })}
        {arr.length > 8 && (
          <div ref={overflowRef} className="relative">
            <button
              type="button"
              onClick={() => setOverflowOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={overflowOpen}
              aria-label={`展开剩余 ${arr.length - 8} 位在线同学`}
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold cursor-pointer hover:opacity-90"
              style={{
                background: theme.surface,
                color: theme.surfaceText,
                border: `2px solid ${theme.surface}`,
                boxShadow: theme.surfaceShadow,
              }}
            >
              +{arr.length - 8}
            </button>
            {overflowOpen && (
              <ul
                role="listbox"
                className="absolute right-0 top-10 z-40 max-h-72 overflow-y-auto rounded-lg backdrop-blur-xl py-1 text-xs"
                style={{
                  minWidth: 180,
                  background: theme.surface,
                  color: theme.surfaceText,
                  border: `1px solid ${theme.surfaceBorder}`,
                  boxShadow: theme.surfaceShadow,
                }}
              >
                {arr.slice(8).map((u) => {
                  const hl = usePresence.getState().highlightUserId === u.user_id;
                  const isMe = me?.id === u.user_id;
                  return (
                    <li key={u.user_id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={hl}
                        disabled={isMe}
                        onClick={() => {
                          if (isMe) return;
                          const cur = usePresence.getState().highlightUserId;
                          usePresence.getState().setHighlight(cur === u.user_id ? null : u.user_id);
                          setOverflowOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-black/10 disabled:opacity-60 disabled:cursor-default text-left"
                        style={{
                          background: hl ? theme.selectedBg : 'transparent',
                        }}
                      >
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                          style={{
                            background: u.color,
                            color: readableOn(u.color),
                            outline: hl ? `2px solid #a78bfa` : 'none',
                          }}
                        >
                          {firstGlyph(displayName(u))}
                        </span>
                        <span className="truncate">{displayName(u)}{isMe ? '（你）' : ''}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="账户菜单"
          aria-expanded={menuOpen}
          className="h-8 px-3 rounded-lg text-xs font-medium backdrop-blur-xl"
          style={{
            background: theme.surface,
            color: theme.surfaceText,
            border: `1px solid ${theme.surfaceBorder}`,
            boxShadow: theme.surfaceShadow,
          }}
        >
          {me ? displayName(me) : '我'} ▾
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 mt-2 min-w-[160px] rounded-lg overflow-hidden"
            style={{
              background: theme.surface,
              color: theme.surfaceText,
              border: `1px solid ${theme.surfaceBorder}`,
              boxShadow: theme.surfaceShadow,
            }}
          >
            <button
              role="menuitem"
              onClick={() => { setMenuOpen(false); setRenameOpen(true); }}
              className="block w-full text-left px-3 py-2 text-xs hover:bg-black/10"
            >
              修改昵称
            </button>
            {me?.is_admin && (
              <button
                role="menuitem"
                onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                className="block w-full text-left px-3 py-2 text-xs hover:bg-black/10"
              >
                管理后台
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                logout();
                navigate('/login', { replace: true });
              }}
              className="block w-full text-left px-3 py-2 text-xs hover:bg-black/10"
            >
              退出登录
            </button>
          </div>
        )}
      </div>
      <NicknameDialog
        open={renameOpen}
        user={me}
        theme={theme}
        onClose={() => setRenameOpen(false)}
        onSaved={(u) => {
          useAuth.setState({ user: u });
          usePresence.getState().addUser({ user_id: u.id, display_name: u.display_name ?? u.username, username: u.username, color: u.color });
        }}
      />
    </div>
  );
}

function NicknameDialog({
  open, user, theme, onClose, onSaved,
}: {
  open: boolean;
  user: User | null;
  theme: ReturnType<typeof getTheme>;
  onClose: () => void;
  onSaved: (u: User) => void;
}) {
  const [name, setName] = useState(user?.display_name ?? '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(user?.display_name ?? ''); setErr(''); } }, [open, user]);
  if (!open || !user) return null;

  async function save() {
    const v = name.trim();
    if (!v) { setErr('昵称不能为空'); return; }
    if (v.length > 24) { setErr('昵称最多 24 个字符'); return; }
    setBusy(true);
    try {
      const token = useAuth.getState().token;
      const r = await fetch('/api/me', {
        method: 'PATCH',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ display_name: v }),
      });
      if (!r.ok) { setErr('保存失败，请稍后再试'); return; }
      onSaved(await r.json());
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const surfaceStyle: React.CSSProperties = {
    background: theme.surface,
    color: theme.surfaceText,
    border: `1px solid ${theme.surfaceBorder}`,
    boxShadow: theme.surfaceShadow,
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 cursor-default" role="dialog" aria-label="修改昵称">
      <div className="bb-pop w-[300px] rounded-2xl p-4" style={surfaceStyle}>
        <div className="text-sm font-semibold mb-3">修改昵称</div>
        <div className="text-[12px] mb-3" style={{ color: theme.surfaceTextMuted }}>
          画板上的作品署名和在线列表都会显示这个昵称（其他同学重新进入后生效）。
        </div>
        <input
          autoFocus
          value={name}
          maxLength={24}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onClose(); }}
          className="w-full h-10 px-3 rounded-lg text-sm outline-none border"
          style={{ background: 'rgba(0,0,0,0.15)', color: theme.surfaceText, borderColor: err ? '#ef4444' : theme.surfaceBorder }}
          placeholder="给自己起个名字"
          aria-label="新昵称"
        />
        {err && <div className="mt-2 text-[12px] text-red-500">{err}</div>}
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="h-9 px-3 rounded-lg text-xs" style={{ color: theme.surfaceTextMuted }}>
            取消
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="h-9 px-4 rounded-lg text-xs font-medium text-white rounded-lg"
            style={{ background: '#6D4C8C' }}
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pick black/white text for best contrast on a solid hex color
// Avatar initial: take the first user-perceived character (code-point safe,
// so emoji surrogate pairs aren't sliced in half). Uppercased only when ASCII.
function firstGlyph(name: string | null | undefined): string {
  if (!name) return '?';
  const first = Array.from(name)[0];
  if (!first) return '?';
  return /[a-z]/.test(first) ? first.toUpperCase() : first;
}

function readableOn(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 3 && h.length !== 6) return '#fff';
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111827' : '#ffffff';
}
